import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Info, Phone } from 'lucide-react';

import defaultImg from "../Images/multiport.jpg";
import img from "../Images/venitube.jpg";
import img1 from "../Images/compactflow.jpg";
import img2 from "../Images/cp.jpg";
import img3 from "../Images/avpt.png";
import img4 from "../Images/vtb.png";
import img5 from "../Images/compact.png";
import img6 from "../Images/stemflow.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState('description');

  return (
    <article className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6">
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">{product.name}</h3>
      </header>
      
      <div className="bg-gray-100 h-40 sm:h-48 md:h-56 mb-4 rounded-lg overflow-hidden">
        <img 
          src={product.mainImage}
          alt={`${product.name} - industrial flow measurement device for accurate flow monitoring`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          loading="lazy"
          width="400"
          height="300"
        />
      </div>

      <div className="mb-4">
        <nav className="flex border-b border-gray-200" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'description'}
            aria-controls={`${product.id}-description`}
            className={`flex-1 py-2 px-2 sm:px-4 text-sm sm:text-base transition-colors duration-300 ${
              activeTab === 'description'
                ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('description')}
          >
            Description
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'specs'}
            aria-controls={`${product.id}-specs`}
            className={`flex-1 py-2 px-2 sm:px-4 text-sm sm:text-base transition-colors duration-300 ${
              activeTab === 'specs'
                ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('specs')}
          >
            Specifications
          </button>
        </nav>

        <div className="mt-4">
          {activeTab === 'description' ? (
            <div id={`${product.id}-description`} role="tabpanel">
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-justify">
                {product.description}
              </p>
            </div>
          ) : (
            <div id={`${product.id}-specs`} role="tabpanel">
              <div className="bg-gray-100 h-32 sm:h-40 rounded-lg overflow-hidden mb-4">
                <img 
                  src={product.specImage}
                  alt={`${product.name} technical specifications and dimensions chart`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="400"
                  height="200"
                />
              </div>
              <ul className="space-y-2">
                {product.specifications.map((spec, index) => (
                  <li key={index} className="flex items-start">
                    <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-xs sm:text-sm text-gray-600">{spec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const FlowMeters = () => {
  const products = [
    {
      id: 'multiport-pitot',
      name: 'Multiport Averaging Pitot Tube',
      mainImage: defaultImg,
      specImage: img3,
      description: `Our Multiport Averaging Pitot Tube delivers highly accurate flow measurement through innovative multiple sensing ports technology. This advanced design provides a true average of flow velocity across the pipe diameter, ensuring exceptional accuracy even in challenging flow conditions. The self-averaging design eliminates the need for straight pipe runs typically required by traditional flow meters, making it ideal for installations with space constraints. The robust construction and no moving parts design ensures long-term reliability with minimal maintenance requirements for industrial applications.`,
      specifications: [
        'Accuracy: ±1% of actual flow rate',
        'Flow turndown ratio up to 10:1',
        'Operating temperature: -50°C to +450°C',
        'Available sizes: DN25 to DN900',
        'Pressure ratings up to Class 2500',
        'Material options: 316SS, Hastelloy, Others',
        'Bi-directional flow capability',
        'Low permanent pressure loss'
      ]
    },
    {
      id: 'venturi-tubes',
      name: 'Venturi Tubes',
      mainImage: img,
      specImage: img4,
      description: `Venturi Tubes represent the pinnacle of reliable flow measurement technology, offering exceptional accuracy with minimal pressure loss. These precision-engineered devices utilize the venturi effect to measure flow rates with remarkable consistency across various industrial processes. The gradually converging and diverging sections minimize permanent pressure loss while providing highly accurate flow measurements. Our venturi tubes are designed for long-term stability and require minimal maintenance, making them ideal for critical flow measurement applications in oil and gas, water treatment, and chemical processing industries.`,
      specifications: [
        'Accuracy: ±0.5% of actual flow rate',
        'Beta ratios: 0.3 to 0.75',
        'Size range: DN50 to DN1200',
        'Pressure recovery up to 90%',
        'ASME MFC-3M compliant design',
        'Multiple material options available',
        'Custom designs for special applications',
        'Calibration certificates available'
      ]
    },
    {
      id: 'compact-flow',
      name: 'Compact Flow Meter',
      mainImage: img1,
      specImage: img5,
      description: `Our Compact Flow Meters provide precise flow measurement in a space-saving design perfect for modern industrial installations. These meters integrate advanced sensing technology with modern electronics to deliver accurate flow measurement in a compact package. The innovative design reduces installation space requirements while maintaining high accuracy and reliability. Perfect for applications where space is limited but accurate flow measurement is crucial, these meters offer easy installation and minimal maintenance requirements while providing reliable digital output signals for modern control systems and SCADA integration.`,
      specifications: [
        'Compact design for space-restricted installations',
        'Digital output: 4-20mA, HART, Modbus',
        'Accuracy: ±0.5% of reading',
        'Turndown ratio: 30:1',
        'Local display with configuration interface',
        'Multiple process connection options',
        'Built-in temperature compensation',
        'Easy in-line maintenance design'
      ]
    },
    {
      id: 'steam-flow',
      name: 'Steam Flow Meters with Condensate Pots',
      mainImage: img2,
      specImage: img6,
      description: `Specialized Steam Flow Meters with integrated condensate pots provide accurate measurement of steam flow while maintaining measurement stability in demanding steam applications. These meters feature advanced condensate management systems that ensure accurate readings regardless of steam conditions. The integrated design protects sensing elements from direct steam contact while maintaining measurement accuracy. Our steam flow meters include temperature and pressure compensation for mass flow measurement, making them ideal for energy management, process control, and steam billing applications in industrial facilities.`,
      specifications: [
        'Mass flow accuracy: ±1% of reading',
        'Steam pressure rating up to 100 bar',
        'Temperature rating up to 450°C',
        'Integrated temperature compensation',
        'Self-draining condensate pots',
        'Available sizes: DN25 to DN300',
        'Saturated and superheated steam measurement',
        'Energy consumption calculation capability'
      ]
    }
  ];

  // Structured data for flow meters
  const productListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Industrial Flow Meters",
    "description": "Precision flow measurement solutions including pitot tubes, venturi tubes, compact meters, and steam flow meters for industrial applications",
    "numberOfItems": products.length,
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.mainImage,
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "price": "Contact for pricing",
          "priceCurrency": "USD"
        }
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://www.gorthermenergy.com/products"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Flow Meters",
        "item": "https://www.gorthermenergy.com/products/flow-meters"
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Optimized title (under 60 characters) */}
        <title>Industrial Flow Meters | Precision Flow Measurement Nepal</title>
        
        {/* Optimized meta description (120-160 characters) */}
        <meta name="description" content="Premium industrial flow meters including pitot tubes, venturi tubes, and steam meters. Accurate flow measurement solutions in Nepal." />
        
        {/* Keywords */}
        <meta name="keywords" content="flow meters, industrial flow measurement, pitot tube, venturi tube, steam flow meter, compact flow meter, flow measurement devices Nepal" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gorthermenergy.com/products/flow-meters" />
        
        {/* Language */}
        <html lang="en" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Industrial Flow Meters | Gortherm Energy" />
        <meta property="og:description" content="Precision flow measurement solutions for industrial applications including steam, liquid, and gas flow meters." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/products/flow-meters" />
        <meta property="og:image" content={defaultImg} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Flow Meters" />
        <meta name="twitter:description" content="Accurate flow measurement solutions for industrial processes" />
        <meta name="twitter:image" content={defaultImg} />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productListSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
      </Helmet>

      <div className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          
          {/* Hero Section */}
          <header className="text-center mb-12 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Industrial Flow Meters
            </h1>
            <h2 className="text-xl sm:text-2xl text-gray-700 mb-4">
              Precision Flow Measurement Solutions
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Explore our comprehensive range of precision flow measurement solutions, engineered to deliver accurate and reliable flow monitoring across all industrial applications. From steam flow meters to compact digital devices, we provide complete flow measurement systems for process control, energy management, and billing applications in Nepal.
            </p>
          </header>

          {/* Product Grid */}
          <section aria-label="Flow meter products catalog">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>

          {/* Additional SEO Content */}
          <section className="mt-12 sm:mt-16 bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Why Choose Our Flow Meters?
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">High Accuracy</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Precision-engineered flow meters delivering accuracy up to ±0.5% for critical process control and energy management applications.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Low Maintenance</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Robust designs with no moving parts ensuring long-term reliability and minimal maintenance requirements in industrial environments.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Wide Range</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Comprehensive selection suitable for liquids, gases, and steam across various pipe sizes and operating conditions.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Expert Support</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Professional installation, calibration services, and ongoing technical support ensuring optimal system performance.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Digital Integration</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Modern digital outputs including 4-20mA, HART, and Modbus for seamless integration with control systems and SCADA.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Cost-Effective</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Competitive pricing with excellent long-term value through energy savings and reduced maintenance costs.
                </p>
              </article>
            </div>
          </section>

          {/* Applications Section */}
          <section className="mt-12 sm:mt-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 md:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Industrial Applications
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Process Industries</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">
                  Critical flow measurement for chemical processing, pharmaceutical manufacturing, food and beverage production, and oil and gas facilities.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Steam Systems</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">
                  Accurate steam flow measurement for energy management, billing applications, and process optimization in industrial boiler systems.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Water Treatment</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">
                  Precise flow monitoring for water and wastewater treatment plants, ensuring optimal process control and regulatory compliance.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">HVAC Systems</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">
                  Reliable flow measurement for heating, ventilation, and air conditioning systems in commercial and industrial buildings.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="mt-12 sm:mt-16 text-center bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Need Expert Guidance?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our flow measurement specialists are here to help you select the perfect flow meter solution for your specific application. Get professional consultation and technical support for your project.
            </p>
            <a href="tel:+9779802781400" className="inline-block">
              <button className="bg-teal-600 hover:bg-teal-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                <Phone className="w-5 h-5" aria-hidden="true" />
                <span className="text-sm sm:text-base font-semibold">Contact Our Experts</span>
              </button>
            </a>
            
          </section>
        </div>
      </div>
    </>
  );
};

export default FlowMeters;